import {
  IonBreadcrumbs, IonBreadcrumb, IonIcon, IonCard, IonCardTitle, IonCardHeader, IonButton, IonCardContent, IonInput, 
  IonModal, IonTextarea, IonProgressBar, ItemReorderEventDetail, IonSelect, IonSelectOption, IonSpinner, IonText 
} from '@ionic/react';
import AppContainer from '../AppContainer';
import { folder, duplicate, desktop, rocket } from 'ionicons/icons';
import Menu from '../components/Menu';
import { useHistory, useLocation } from 'react-router';
import { useCallback, useContext, useEffect, useState } from 'react';
import GlobalContext from '../domain/GlobalContext';
import { api } from '../domain/ApiService';
import { Artifact, Assistant, Initiative, Template } from '../domain/Types';  // Assuming Template is defined in Types
import ListView from '../components/ListView';
import { v4 as uuidv4 } from 'uuid';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Artifacts from '../components/Artifacts';

const Page: React.FC = () => {
  const history = useHistory();
  const { initiative, initiatives, setInitiative } = useContext(GlobalContext);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isFetched, setIsFetched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [artifact, setArtifact] = useState<Artifact | null>(null);

  const [showPowerPointModal, setShowPowerPointModal] = useState(false);
  const [template, setTemplate] = useState<Template | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchArtifacts = useCallback(async () => {
    try {
      if (!initiative?.assistants) return;

      const response: Artifact[] = await api('/artifact/list', {}, initiative);
      setIsFetched(false);
      for (const assistant of initiative?.assistants) {
        if (!assistant) continue;
        assistant.artifacts = response.filter(artifact => artifact.assistant_uid === assistant.uid);
      }
      setIsFetched(true);
    } catch (err) {
      console.error('Error fetching:', err);
    }
  }, [initiative, assistants]);

  useEffect(() => {
    if (initiative) {
      fetchArtifacts();
      fetchTemplate();
    }
  }, [initiative, assistants, fetchArtifacts]);

  // Fetch existing template for the initiative
  const fetchTemplate = async () => {
    try {
      const response: Template = await api('/export/templates/get', { initiative_uid: initiative?.uid }, initiative);
      setTemplate(response);
    } catch (err) {
      console.error('Error fetching template:', err);
    }
  };

  const [isTemplateLoaded, setIsTemplateLoaded] = useState(true);
  const handleUploadTemplate = async (event: React.ChangeEvent<HTMLInputElement>) => {

    if (!initiative) return;

    setIsTemplateLoaded(false);
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];

    const storage = getStorage();
    const newTemplateUid = uuidv4();
    const filePath = `templates/${initiative.instance_id}/${newTemplateUid}`;
    const storageRef = ref(storage, filePath);

    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Template uploaded:', downloadURL);

    const newTemplate: Template = {
      instance_id: initiative.instance_id,
      uid: newTemplateUid,
      initiative_uid: initiative.uid,
      file_url: downloadURL,
      name: file.name,
      type: 'pptx'
    };

    const savedTemplate = await api('/export/templates/create', newTemplate, initiative);
    setTemplate(savedTemplate);
    setIsTemplateLoaded(true);
  };

  // Export PowerPoint based on the saved template
  const handleExportPowerPoint = async () => {
    setIsExporting(true);
    try {
      const response = await api('/export/templates/export', { template_id: template?.uid }, initiative);
      console.log("Export response ("+ template?.uid +"):", response);  // Log the response to debug
      const downloadUrl = await fetchSignedUrl(response.download_url);
      console.log("Signed URL:", downloadUrl);
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      } else {
        console.error("Export failed: No download URL provided.");
      }
    } catch (error) {
      console.error('Error exporting PowerPoint:', error);
    }
    setIsExporting(false);
    setShowPowerPointModal(false);
  };
  // Remove the current template
  const removeTemplate = async () => {
    try {
      await api('/export/templates/remove', { template_id: template?.uid }, initiative);
      setTemplate(null);
    } catch (error) {
      console.error('Error removing template:', error);
    }
  };

  const [html, setHtml] = useState<string>(); // Add this state variable if it's not already defined

  // Add handlePrint function
  const handlePrint = async () => {
    setIsFetched(false);
    try {
      const response: any = await api("/export/html", {}, initiative);
      setIsFetched(true);
      setHtml(response.html);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      if (iframe && iframe.contentDocument && iframe.contentWindow && response.html) {
        const cssResponse = await fetch('/assets/pdf.css');
        const cssText = await cssResponse.text();

        const completeHtml = `
          <!DOCTYPE html>
          <html>
            <head>
              <style>
                @page { margin: 0px; padding: 100px; }
                @media print { ${cssText} }
              </style>          
            </head>
            <body>
              ${response.html}
            </body>
          </html>
        `;
        iframe.contentDocument.write(completeHtml);
        iframe.contentDocument.close();

        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        iframe.contentWindow.onafterprint = () => {
          document.body.removeChild(iframe);
        };
      }
    } catch (error) {
      console.error('Error during print:', error);
    }
    setIsFetched(true);
  };

  // Add handleDownloadMd function
  const handleDownloadMd = async () => {
    try {
      setIsFetched(false);
      const response = await api("/export/md", {}, initiative);
      setIsFetched(true);
      const blob = new Blob([response.md], { type: 'text/markdown' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);  
      link.href = url;
      link.download = "export.md";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);  
      URL.revokeObjectURL(url);
    } 
    catch (err) {
      setIsFetched(true);
      console.error('Error generating file:', err);
    }
  };  

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleClick = (clickedArtifact: Artifact) => {
    setArtifact(clickedArtifact);
    setShowModal(true);
  };

  const updateArtifact = async () => {
    setShowModal(false);
    if (!artifact) return;
    setIsFetched(false);

    if (artifact.id)
      await api('/artifact/update', artifact, initiative);
    else
      await api('/artifact/create', artifact, initiative);

    setIsFetched(true);
  };

  const removeArtifact = async () => {
    setShowModal(false);
    if (!artifact) return;
    setIsFetched(false);
    const response: Artifact = await api('/artifact/remove', artifact, initiative);
    await fetchArtifacts();
    setIsFetched(true);
  };

  const fetchSignedUrl = async (image_url: string) => {
    if (!image_url) return null;
    const regex = /https:\/\/storage\.googleapis\.com\/[^\/]+\/(.+)/;
    const match = image_url.match(regex);
    const imagePath = match ? match[1] : null;

    if (imagePath) {
      const storage = getStorage();
      const storageRef = ref(storage, imagePath);
      try {
        const url = await getDownloadURL(storageRef);
        return url;
      } catch (error) {
        console.error('Error fetching signed URL:', error);
      }
    }
    return null;
  };

  const [urls, setUrls] = useState<{ [key: string]: string | null }>({});
  useEffect(() => {
    const fetchUrls = async () => {
      if (!initiative?.assistants) return;
      const newUrls: { [key: string]: string | null } = {};
      for (const assistant of initiative?.assistants) {
        if (!assistant.artifacts) continue;
        for (const artifact of assistant?.artifacts) {
          if (!artifact.image_url) continue;
          const url = await fetchSignedUrl(artifact.image_url);
          newUrls[artifact.uid] = url;
        }
      }
      setUrls(newUrls);
    };
    if (initiative?.assistants) fetchUrls();
  }, [initiative]);

  return (
    <>
      <div style={{ margin: "8px" }}>
        <IonButton className="basic" onClick={handlePrint}>
          Print & PDF
        </IonButton>
        <IonButton className="basic" onClick={() => setShowPowerPointModal(true)}>
          Export PowerPoint
        </IonButton>
        <IonButton className="basic" onClick={handleDownloadMd}>
          Download Markdown
        </IonButton>
      </div>

      {(initiative != null) ? (
        <>
          {initiative?.assistants?.map(assistant => (
            <IonCard className='filled' style={{ padding: "10px" }} key={assistant.uid}>
              <IonCardHeader>
                <IonCardTitle>{assistant.name}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>

                <Artifacts assistant={assistant} preloadedArtifacts={assistant.artifacts} />

                {/* 
                <ListView<Artifact>
                  items={assistant.artifacts}
                  artifact_parameters={assistant.artifact_parameters}
                  enableReorder={false}
                  defaultLayout={'table'}
                  showActions={true}
                  onEdit={handleEdit}                    
                  onClick={handleClick}
                  onReorder={handleReorder}
                />
                */}
              </IonCardContent>
            </IonCard>
          ))}

          <IonModal className="form-modal" onDidDismiss={() => setShowModal(false)} isOpen={showModal}>
            {(!loadingModal && artifact) ? 
              <div className="form">
                <IonTextarea 
                  className='form-element'
                  label="Artifact"
                  labelPlacement="floating"
                  fill="outline"
                  rows={15}            
                  value={JSON.stringify(artifact.content_json, null, 2)}
                  onIonChange={e => artifact.content_json = e.detail.value!} 
                  placeholder="Artifact"
                />                    
                <IonButton onClick={updateArtifact}>Save</IonButton>
                <IonButton className="basic" onClick={handleCancel}>Cancel</IonButton>
                <IonButton color="danger" onClick={removeArtifact}>Remove</IonButton>
              </div>
              :
              <div className="form" style={{ minHeight: "160px" }}>
                <div className="form-element-title" style={{ opacity: "0.5", width: "100%", textAlign: "center" }}>                  
                  <IonIcon icon={duplicate} style={{ width: "60px", height: "60px" }} className="menu" color={"medium"} />            
                </div>
                <div className="form-element-title" style={{ opacity: "0.5", width: "100%", textAlign: "center" }}>
                  Preparing ...
                </div>
                <div className="form-element-title" style={{ width: "100%", textAlign: "center" }}>
                  <IonSpinner name="dots" color="dark" className="large-spinner" style={{ margin: "10px" }}></IonSpinner>
                </div>
              </div>
            }
          </IonModal>

          <IonModal className="form-modal" onDidDismiss={() => setShowPowerPointModal(false)} isOpen={showPowerPointModal}>
            <div className="form">
              <big>Export PowerPoint</big>
              <p>
              <div style={{ opacity: "0.6", fontSize: "90%", marginBottom: "5px" }}>Upload a template where content will be exported to:</div>
              <div className='form-file-upload'> 
              {
                !isTemplateLoaded ?
                  <IonSpinner name="dots" color="dark"></IonSpinner>
                :
                (template != null && template.name != null ?                 
                  <>{template.name}</> 
                :
                 <>
                  <input type="file" accept=".pptx" onChange={handleUploadTemplate} />
                  </>
                )
              }
                
              </div>

              </p>
              <IonButton onClick={handleExportPowerPoint} disabled={!template || isExporting}>
                Export to template
              </IonButton>
              <IonButton className="basic" onClick={() => setShowPowerPointModal(false)}>Cancel</IonButton>
              {template != null && template.name != null && (
                <IonButton color="danger" onClick={removeTemplate}>Remove Template</IonButton>
              )}
            </div>
          </IonModal>
        </>
      ) : (
        <>{/* <IonProgressBar type="indeterminate" color="medium"></IonProgressBar> */}</>
      )}
    </>
  );
};

const Breadcrumb: React.FC = () => {
  const { initiative } = useContext(GlobalContext);
  const location = useLocation();

  return (
    <IonBreadcrumbs>
      <IonBreadcrumb routerLink={"/"}>
        <IonIcon slot="start" icon={rocket}></IonIcon>
        Base
      </IonBreadcrumb>
      <IonBreadcrumb routerLink={"../" + initiative?.slug}>
        <IonIcon slot="start" icon={desktop}></IonIcon>
        {initiative?.name}
      </IonBreadcrumb>
      <IonBreadcrumb>
        <IonIcon slot="start" icon={folder}></IonIcon>
        {initiative?.deliverable || "Deliverable"}
      </IonBreadcrumb>
    </IonBreadcrumbs>   
  );
};

const Container: React.FC<{}> = () => (
  <AppContainer ContentComponent={Page} BreadcrumbComponent={Breadcrumb} MenuComponent={Menu} />
);

export default Container;
